import { convertMelodyTapGestures, type MelodyTapRawGesture } from "./melody-tap.ts";
import { GRID_BEATS, type GridValue, type MidiNote, type MidiNoteSource, type PitchFrame } from "./music.ts";
import { advanceVoicePitchFrame, EMPTY_VOICE_DISPLAY_STATE } from "./note-events.ts";

export const RHYTHM_SLOT_PPQ = 480;
export const RHYTHM_SLOT_HOLD_MS = 180;
const BOUNDARY_GUARD_SECONDS = 0.045;

export type RhythmSlotSource = "ORIGINAL_SEGMENTATION" | "TAP_REFINE" | "RHYTHM_FIRST" | "MANUAL_EDIT";
export type RhythmSlotStatus = "RESOLVED" | "UNRESOLVED";

export type PitchEvidenceFrame = PitchFrame & {
  cents: number | null;
  voiced: boolean;
};

export type RhythmSlot = {
  id: string;
  rawStartTick?: number;
  rawEndTick?: number;
  quantizedStartTick?: number;
  quantizedEndTick?: number;
  refinedStartTick?: number;
  refinedEndTick?: number;
  refinement?: "LOCAL_PATTERN";
  startTick: number;
  endTick: number;
  durationTicks: number;
  startBeat: number;
  durationBeats: number;
  source: RhythmSlotSource;
  confidence: number;
  pitch: number | null;
  status: RhythmSlotStatus;
};

export type RhythmSlotPitchAssignmentOptions = {
  bpm: number;
  confidenceThreshold?: number;
  minimumFrames?: number;
  minimumWinnerShare?: number;
};

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function lightSnapBeat(beat: number, gridBeats: number): number {
  const nearest = Math.round(beat / gridBeats) * gridBeats;
  return Math.abs(nearest - beat) <= gridBeats * 0.3 ? nearest : beat;
}

export function retainRawPitchEvidence(frames: PitchFrame[], confidenceThreshold = 0.55): PitchEvidenceFrame[] {
  return frames.map((frame) => {
    const threshold = frame.acceptanceConfidence ?? confidenceThreshold;
    const voiced = frame.frequency !== null && frame.midiFloat !== null && frame.confidence >= threshold;
    const nearestMidi = frame.midiFloat === null ? null : Math.round(frame.midiFloat);
    return {
      ...frame,
      cents: frame.cents ?? (nearestMidi === null ? null : Math.round(((frame.midiFloat as number) - nearestMidi) * 100)),
      voiced: frame.voiced ?? voiced,
    };
  });
}

export function buildRhythmSlotsFromMelodyTaps(
  rawGestures: MelodyTapRawGesture[],
  bpm: number,
  options: {
    source: Extract<RhythmSlotSource, "TAP_REFINE" | "RHYTHM_FIRST">;
    grid?: GridValue;
    originTimeMs?: number;
    finalDurationSeconds?: number;
    ppq?: number;
  },
): RhythmSlot[] {
  if (!rawGestures.length) return [];
  const safeBpm = Math.max(1, bpm);
  const beatMs = 60_000 / safeBpm;
  const ppq = options.ppq ?? RHYTHM_SLOT_PPQ;
  const orderedRaw = [...rawGestures].sort((a, b) => a.startTime - b.startTime || a.id - b.id);
  const converted = convertMelodyTapGestures(orderedRaw, safeBpm);
  const origin = options.originTimeMs ?? orderedRaw[0].startTime;
  const firstOffsetBeats = (orderedRaw[0].startTime - origin) / beatMs;
  const fallbackBeats = GRID_BEATS[options.grid ?? "1/8"];
  const gridBeats = GRID_BEATS[options.grid ?? "1/8"];
  const captureEndBeat = Math.max(0, (options.finalDurationSeconds ?? 0) * safeBpm / 60);

  return converted.map((gesture, index) => {
    const rawStartBeat = firstOffsetBeats + gesture.startBeat;
    const startBeat = lightSnapBeat(rawStartBeat, gridBeats);
    const nextRawStart = converted[index + 1] ? firstOffsetBeats + converted[index + 1].startBeat : null;
    const held = gesture.durationMs >= RHYTHM_SLOT_HOLD_MS;
    const heldEnd = rawStartBeat + Math.max(fallbackBeats, gesture.durationBeats);
    let rawEndBeat = held ? heldEnd : (nextRawStart ?? Math.max(rawStartBeat + fallbackBeats, captureEndBeat));
    if (nextRawStart !== null) rawEndBeat = held ? Math.min(rawEndBeat, nextRawStart) : nextRawStart;
    const endBeat = Math.max(startBeat + 1 / 32, lightSnapBeat(rawEndBeat, gridBeats));
    const startTick = Math.round(startBeat * ppq);
    const endTick = Math.max(startTick + 1, Math.round(endBeat * ppq));
    const rawStartTick = Math.round(rawStartBeat * ppq);
    const rawEndTick = Math.max(rawStartTick + 1, Math.round(rawEndBeat * ppq));
    return {
      id: `${options.source.toLowerCase()}-${gesture.id}-${startTick}`,
      startTick,
      rawStartTick,
      rawEndTick,
      quantizedStartTick: startTick,
      quantizedEndTick: endTick,
      endTick,
      durationTicks: endTick - startTick,
      startBeat: startTick / ppq,
      durationBeats: (endTick - startTick) / ppq,
      source: options.source,
      confidence: held ? 0.92 : 0.82,
      pitch: null,
      status: "UNRESOLVED",
    };
  });
}

function frameDurations(frames: PitchEvidenceFrame[]): number[] {
  const deltas = frames.slice(1).map((frame, index) => frame.time - frames[index].time).filter((value) => value > 0 && value <= 0.2);
  const sorted = [...deltas].sort((a, b) => a - b);
  const fallback = clamp(sorted[Math.floor(sorted.length / 2)] ?? 0.055, 0.01, 0.1);
  return frames.map((frame, index) => clamp(frames[index + 1]?.time - frame.time || fallback, 0.01, 0.15));
}

export function assignPitchEvidenceToSlots(
  inputFrames: PitchFrame[],
  slots: RhythmSlot[],
  options: RhythmSlotPitchAssignmentOptions,
): RhythmSlot[] {
  const confidenceThreshold = options.confidenceThreshold ?? 0.55;
  const frames = retainRawPitchEvidence([...inputFrames].sort((a, b) => a.time - b.time), confidenceThreshold);
  const durations = frameDurations(frames);
  let stabilizer = { ...EMPTY_VOICE_DISPLAY_STATE };
  const stableNotes = frames.map((frame) => {
    stabilizer = advanceVoicePitchFrame(stabilizer, frame.voiced ? frame : null, confidenceThreshold, 2, 3);
    return stabilizer.stableNote;
  });
  const secondsPerBeat = 60 / Math.max(1, options.bpm);

  return slots.map((slot) => {
    const startTime = slot.startBeat * secondsPerBeat;
    const endTime = (slot.startBeat + slot.durationBeats) * secondsPerBeat;
    const scores = new Map<number, { score: number; frames: number }>();
    frames.forEach((frame, index) => {
      if (!frame.voiced || frame.midiFloat === null || frame.time < startTime || frame.time >= endTime) return;
      const stable = stableNotes[index];
      const midi = stable !== null && Math.abs(frame.midiFloat - stable) <= 0.45 ? stable : Math.round(frame.midiFloat);
      const edgeDistance = Math.min(frame.time - startTime, endTime - frame.time);
      const boundaryWeight = edgeDistance < BOUNDARY_GUARD_SECONDS ? 0.35 : 1;
      const centeringWeight = Math.max(0.35, 1 - Math.abs(frame.midiFloat - midi) * 0.65);
      const weight = Math.max(0.01, frame.confidence) * durations[index] * boundaryWeight * centeringWeight;
      const current = scores.get(midi) ?? { score: 0, frames: 0 };
      current.score += weight;
      current.frames += 1;
      scores.set(midi, current);
    });
    const ranked = [...scores.entries()].sort((a, b) => b[1].score - a[1].score || b[1].frames - a[1].frames);
    const total = ranked.reduce((sum, [, value]) => sum + value.score, 0);
    const winner = ranked[0];
    const winnerShare = winner ? winner[1].score / Math.max(0.000001, total) : 0;
    const resolved = Boolean(winner)
      && winner[1].frames >= (options.minimumFrames ?? 2)
      && winnerShare >= (options.minimumWinnerShare ?? 0.58);
    return {
      ...slot,
      pitch: resolved ? winner[0] : null,
      status: resolved ? "RESOLVED" : "UNRESOLVED",
      confidence: resolved ? clamp(winnerShare, 0, 1) : Math.min(slot.confidence, winnerShare),
    };
  });
}

function sourceForNote(source: RhythmSlotSource): MidiNoteSource {
  if (source === "TAP_REFINE") return "TAP_REFINE";
  if (source === "RHYTHM_FIRST") return "RHYTHM_FIRST";
  if (source === "MANUAL_EDIT") return "MANUAL";
  return "AUTO";
}

export function rhythmSlotsToEditableNotes(slots: RhythmSlot[], unresolvedDisplayMidi = 60): MidiNote[] {
  return slots.map((slot) => ({
    midi: slot.pitch ?? unresolvedDisplayMidi,
    pitch: slot.pitch,
    status: slot.status,
    source: sourceForNote(slot.source),
    rhythmSlotId: slot.id,
    startBeat: slot.startBeat,
    durationBeats: slot.durationBeats,
    rawStartTick: slot.rawStartTick,
    rawEndTick: slot.rawEndTick,
    quantizedStartTick: slot.quantizedStartTick ?? slot.startTick,
    quantizedEndTick: slot.quantizedEndTick ?? slot.endTick,
    refinedStartTick: slot.refinedStartTick,
    refinedEndTick: slot.refinedEndTick,
    refinement: slot.refinement,
    velocity: slot.pitch === null ? 0 : Math.round(82 + clamp(slot.confidence, 0, 1) * 28),
  }));
}

export function resolveEditableNoteWithVoice(note: MidiNote, midi: number): MidiNote {
  return { ...note, midi, pitch: midi, status: "RESOLVED", source: "VOICE", velocity: note.velocity > 0 ? note.velocity : 92 };
}